import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { api, setAccessToken as setAxiosToken } from '../lib/axios';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  tier: string;
  tenantId: string | null;
  role: string;
  isActive: boolean;
  mustChangePassword: boolean;
  setupComplete?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  setAccessToken: (token: string) => void;
  refreshUser: () => Promise<void>;
}

// The dealer POS is dealer-only. Operator/Xyra users (no tenant) belong in the Admin app at
// /admin and must never establish a session here — neither via direct login nor via the
// refresh cookie, which is shared across both apps on this domain.
const isDealer = (u: AuthUser | null | undefined): boolean => !!u && u.tier === 'dealer';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isLoading: true,
  });
  const initDone = useRef(false);

  // On mount, try to restore session via refresh token (HttpOnly cookie)
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    const restore = async () => {
      try {
        const res = await api.post<{ data: { accessToken: string } }>('/auth/refresh');
        const token = res.data.data.accessToken;
        const meRes = await api.get<{ data: AuthUser }>('/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const restored = meRes.data.data;
        // A shared refresh cookie may belong to an operator/xyra session created in /admin.
        // Don't adopt it here — treat as logged out. Do NOT call logout(): that would revoke
        // the shared refresh token and break the legitimate Admin session.
        if (!isDealer(restored)) {
          setAxiosToken(null);
          setState({ user: null, accessToken: null, isLoading: false });
          return;
        }
        // Prime the axios module token so the first page fetch carries Authorization and
        // doesn't 401 into a refresh that races this restore().
        setAxiosToken(token);
        setState({ user: restored, accessToken: token, isLoading: false });
      } catch {
        setAxiosToken(null);
        setState({ user: null, accessToken: null, isLoading: false });
      }
    };

    restore();
  }, []);

  const login = useCallback(async (email: string, password: string, rememberDevice = true) => {
    const res = await api.post<{ data: { accessToken: string; user: AuthUser } }>('/auth/login', {
      email,
      password,
      app: 'pos',
      rememberDevice,
    });
    const { accessToken, user } = res.data.data;
    // Backend already rejects non-dealers for app:'pos'; this is defence-in-depth so a
    // non-dealer can never be set as the POS session even if that check regresses.
    if (!isDealer(user)) {
      throw new Error('invalid_credentials');
    }
    setAxiosToken(accessToken);
    setState({ user, accessToken, isLoading: false });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    setAxiosToken(null);
    setState({ user: null, accessToken: null, isLoading: false });
  }, []);

  const setAccessToken = useCallback((token: string) => {
    setAxiosToken(token);
    setState((prev) => ({ ...prev, accessToken: token }));
  }, []);

  const refreshUser = useCallback(async () => {
    if (!state.accessToken) return;
    const res = await api.get<{ data: AuthUser }>('/auth/me');
    setState((prev) => ({ ...prev, user: res.data.data }));
  }, [state.accessToken]);

  return (
    <AuthContext.Provider
      value={{ ...state, login, logout, setAccessToken, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
